import React, { useState, useEffect, useContext } from 'react';
import { GlobalContext } from 'context/userContext';
import { DrawerHeader } from '@components/drawer-header';
import {
    Alert, Button, Card, CardContent, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, Fab, Grid, IconButton, MenuItem,
    Stack, TextField, Typography, Chip, Tooltip, Autocomplete, Box, Paper, useTheme
} from '@mui/material';
import DataTable, { TableColumn } from 'react-data-table-component';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CommentIcon from '@mui/icons-material/Comment';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import { listTickets, createTicket, closeTicket, addComment, Ticket, Comment, Attachment } from './api';
import { getEquipaments, Equipament } from '@pages/checklist-config/api';
import { API_URL } from '@service/axios-instance';

export function TicketSystemPage() {
    const { operation } = useContext(GlobalContext);

    // States
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Create Modal
    const [openCreate, setOpenCreate] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);

    // Create Form Data
    const [tipoProblema, setTipoProblema] = useState('');
    const [priority, setPriority] = useState('Medium');
    const [descricao, setDescricao] = useState('');

    // Device Selection (Mixed: Select or Text)
    const [deviceOptions, setDeviceOptions] = useState<Equipament[]>([]);
    const [selectedDevice, setSelectedDevice] = useState<Equipament | null>(null); // For autocomplete object
    const [localizacaoText, setLocalizacaoText] = useState(''); // Only used if no device selected or user types custom

    // Attachments
    const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);

    // Close Modal
    const [openClose, setOpenClose] = useState(false);
    const [ticketToClose, setTicketToClose] = useState<Ticket | null>(null);
    const [closeMsg, setCloseMsg] = useState('');
    const [closeReason, setCloseReason] = useState('');

    // Detail/Chat Modal
    const [openDetail, setOpenDetail] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
    const [newComment, setNewComment] = useState('');
    const [commentFiles, setCommentFiles] = useState<FileList | null>(null);

    const dbReady = !!operation;

    useEffect(() => {
        if (dbReady) {
            fetchTickets();
            fetchDevices();
        }
    }, [dbReady, operation]);

    const fetchTickets = async () => {
        setLoading(true);
        try {
            if (!operation) return;
            const data = await listTickets(operation);
            setTickets(data);
        } catch (e: any) {
            setError('Erro ao carregar tickets.');
        } finally {
            setLoading(false);
        }
    };

    const fetchDevices = async () => {
        try {
            const devs = await getEquipaments(operation || '', {});
            setDeviceOptions(devs);
        } catch (e) {
            console.error("Erro ao carregar equipamentos", e);
        }
    };

    const handleCreate = async () => {
        if (!tipoProblema || !descricao) return;

        // Logic for "Localização": use Device ID/Name if selected, otherwise use text
        let loc = localizacaoText;
        if (selectedDevice) {
            loc = `${selectedDevice.tipo} - ${selectedDevice.id}`; // Format: Type - ID
        } else if (!loc) {
            // If neither, block? Reference says required.
            // If strictly autocomplete, user might get stuck.
            alert("Selecione um equipamento ou digite uma localização.");
            return;
        }

        const formData = new FormData();
        formData.append('tipo_problema', tipoProblema);
        formData.append('localizacao', loc);
        formData.append('descricao', descricao);
        formData.append('priority', priority);

        if (selectedFiles) {
            for (let i = 0; i < selectedFiles.length; i++) {
                formData.append('files', selectedFiles[i]);
            }
        }

        setCreateLoading(true);
        try {
            if (!operation) throw new Error("Operação não selecionada");
            await createTicket(operation, formData);
            setOpenCreate(false);
            resetCreateForm();
            fetchTickets();
        } catch (e: any) {
            alert('Erro ao criar ticket: ' + (e.response?.data?.error || e.message));
        } finally {
            setCreateLoading(false);
        }
    };

    const resetCreateForm = () => {
        setTipoProblema('');
        setPriority('Medium');
        setDescricao('');
        setSelectedDevice(null);
        setLocalizacaoText('');
        setSelectedFiles(null);
    };

    const handleCloseTicket = async () => {
        if (!ticketToClose || !closeMsg || !closeReason) return;
        try {
            if (!operation) return;
            await closeTicket(operation, ticketToClose._id, closeMsg, closeReason);
            setOpenClose(false);
            setTicketToClose(null);
            setCloseMsg('');
            setCloseReason('');
            fetchTickets();
            if (selectedTicket && selectedTicket._id === ticketToClose._id) {
                setOpenDetail(false);
            }
        } catch (e) {
            alert("Erro ao fechar ticket.");
        }
    };

    const handleAddComment = async () => {
        if (!selectedTicket || !newComment) return;
        const formData = new FormData();
        formData.append('message', newComment);
        if (commentFiles) {
            for (let i = 0; i < commentFiles.length; i++) {
                formData.append('files', commentFiles[i]);
            }
        }

        try {
            if (!operation) return;
            await addComment(operation, selectedTicket._id, formData);
            setNewComment('');
            setCommentFiles(null);
            // Refresh ticket data? ideally yes.
            // For now, full refresh
            fetchTickets();
            setOpenDetail(false); // Close/Reopen to refresh? Or update local state?
            // Simple hack: close for now or fetch single ticket. 
            // Since we dont have single fetch, just close.
        } catch (e) {
            alert("Erro ao enviar comentário.");
        }
    };

    const renderAttachments = (files: Attachment[]) => {
        if (!files || files.length === 0) return null;
        return (
            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                {files.map((f, i) => (
                    <Box key={i} sx={{ border: '1px solid #eee', borderRadius: 1, p: 0.5 }}>
                        <a href={`${API_URL}${f.url}`} target="_blank" rel="noopener noreferrer">
                            <Typography variant="caption">{f.filename}</Typography>
                        </a>
                    </Box>
                ))}
            </Stack>
        );
    };

    // Stats calculation
    const totalOpen = tickets.filter(t => t.status === 'aberto').length;
    const totalCritical = tickets.filter(t => t.status === 'aberto' && t.priority === 'Critical').length;
    const waitingSupport = tickets.filter(t => t.status === 'aberto' && t.sub_status === 'Aguardando Suporte').length;
    const waitingOp = tickets.filter(t => t.status === 'aberto' && t.sub_status === 'Aguardando Operação').length;

    // Advanced Stats per Priority
    const priorities = ['Low', 'Medium', 'High', 'Critical'];
    const stats: any = {
        Low: { count: 0, closedCount: 0, sumDuration: 0, maxDuration: 0, sumSupport: 0, sumOps: 0, supportCount: 0, opsCount: 0 },
        Medium: { count: 0, closedCount: 0, sumDuration: 0, maxDuration: 0, sumSupport: 0, sumOps: 0, supportCount: 0, opsCount: 0 },
        High: { count: 0, closedCount: 0, sumDuration: 0, maxDuration: 0, sumSupport: 0, sumOps: 0, supportCount: 0, opsCount: 0 },
        Critical: { count: 0, closedCount: 0, sumDuration: 0, maxDuration: 0, sumSupport: 0, sumOps: 0, supportCount: 0, opsCount: 0 }
    };

    tickets.forEach(t => {
        const p = t.priority || 'Medium'; // Default if missing
        if (!stats[p]) stats[p] = { count: 0, closedCount: 0, sumDuration: 0, maxDuration: 0, sumSupport: 0, sumOps: 0, supportCount: 0, opsCount: 0 };

        stats[p].count++;

        if (t.time_support && t.time_support > 0) {
            stats[p].sumSupport += t.time_support;
            stats[p].supportCount++;
        }

        if (t.time_ops && t.time_ops > 0) {
            stats[p].sumOps += t.time_ops;
            stats[p].opsCount++;
        }

        if (t.status !== 'aberto' && t.total_duration) {
            stats[p].closedCount++;
            stats[p].sumDuration += t.total_duration;
            if (t.total_duration > stats[p].maxDuration) {
                stats[p].maxDuration = t.total_duration;
            }
        }
    });

    const formatTime = (minutes: number) => {
        if (!minutes) return '0min';
        const h = Math.floor(minutes / 60);
        const m = Math.round(minutes % 60);
        if (h > 0) return `${h}h ${m}m`;
        return `${m}m`;
    };

    const StatusRow = ({ label, value }: { label: string, value: string }) => (
        <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" color={label === 'Critical' ? 'error' : label === 'High' ? 'warning.dark' : 'textSecondary'}>
                {label}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
                {value}
            </Typography>
        </Box>
    );

    const StatCard = ({ title, metricKey, isMax = false, isAvg = false, isCount = false }: any) => (
        <Card sx={{ flex: 1, height: '100%' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                    {title}
                </Typography>
                {priorities.map(p => {
                    let val = 0;
                    if (metricKey === 'closing') {
                        if (isMax) val = stats[p].maxDuration;
                        else val = stats[p].closedCount ? stats[p].sumDuration / stats[p].closedCount : 0;
                    } else if (metricKey === 'support') {
                        if (isAvg) val = stats[p].supportCount ? stats[p].sumSupport / stats[p].supportCount : 0;
                        else val = stats[p].sumSupport;
                    } else if (metricKey === 'ops') {
                        if (isAvg) val = stats[p].opsCount ? stats[p].sumOps / stats[p].opsCount : 0;
                        else val = stats[p].sumOps;
                    }
                    return <StatusRow key={p} label={p} value={formatTime(val)} />;
                })}
            </CardContent>
        </Card>
    );

    const columns: TableColumn<Ticket>[] = [
        {
            name: 'Ações',
            cell: row => (
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Ver Detalhes/Chat">
                        <IconButton size="small" onClick={() => { setSelectedTicket(row); setOpenDetail(true); }}>
                            <CommentIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {row.status === 'aberto' && (
                        <Tooltip title="Fechar Ticket">
                            <IconButton size="small" color="error" onClick={() => { setTicketToClose(row); setOpenClose(true); }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            )
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            width: '100px',
            cell: row => (
                <Chip
                    label={row.status}
                    color={row.status === 'aberto' ? 'success' : 'default'}
                    size="small"
                />
            )
        },
        {
            name: 'Sub-Status', selector: row => row.sub_status || '-', sortable: true, width: '160px', cell: row => (
                <Chip
                    label={row.sub_status || '-'}
                    variant="outlined"
                    size="small"
                    color={row.sub_status === 'Aguardando Suporte' ? 'error' : row.sub_status === 'Aguardando Operação' ? 'warning' : 'default'}
                />
            )
        },
        { name: 'Prioridade', selector: row => row.priority, sortable: true, width: '100px' },
        { name: 'Tipo', selector: row => row.tipo_problema, sortable: true, width: '150px' },
        { name: 'Tempo Suporte (min)', selector: row => row.time_support || 0, sortable: true, width: '140px', cell: row => Math.round(row.time_support || 0) },
        { name: 'Tempo Ops (min)', selector: row => row.time_ops || 0, sortable: true, width: '130px', cell: row => Math.round(row.time_ops || 0) },
        { name: 'Total (min)', selector: row => row.total_duration || 0, sortable: true, width: '110px', cell: row => Math.round(row.total_duration || 0) },
        { name: 'Equipamento', selector: row => row.localizacao, sortable: true, width: '180px' },
        { name: 'Abertura', selector: row => new Date(row.created_at).toLocaleString(), sortable: true, width: '170px' },
        { name: 'Atualização/Fechamento', selector: row => row.updated_at ? new Date(row.updated_at).toLocaleString() : '-', sortable: true, width: '170px' },

    ];

    const theme = useTheme();

    return (
        <DrawerHeader title="Assistência Técnica">
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                    <Paper
                        elevation={2}
                        sx={{
                            p: { xs: 2.5, md: 3 },
                            bgcolor: 'background.paper',
                            borderLeft: `6px solid ${theme.palette.primary.main}`,
                            borderRadius: 2,
                            mb: 1
                        }}
                    >
                        <Box>
                            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 600 }}>
                                GESTÃO DE MANUTENÇÃO
                            </Typography>
                            <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Sistema de Tickets</Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                                Abra chamados técnicos, acompanhe o SLA de atendimento e interaja com a equipe de suporte. Centralize a resolução de problemas de hardware e configuração da telemetria.
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
            <Stack spacing={2}>
                <Box display="flex" justifyContent="flex-end">
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenCreate(true)}
                    >
                        Novo Ticket
                    </Button>
                </Box>

                <Stack direction="row" spacing={2} width="100%">
                    <Card sx={{ flex: 1, bgcolor: '#e1f5fe' }}>
                        <CardContent>
                            <Typography variant="subtitle2" color="textSecondary">Tickets Abertos</Typography>
                            <Typography variant="h4">{totalOpen}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flex: 1, bgcolor: '#ffebee' }}>
                        <CardContent>
                            <Typography variant="subtitle2" color="textSecondary">Prioridade Crítica</Typography>
                            <Typography variant="h4">{totalCritical}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flex: 1, bgcolor: '#fff3e0' }}>
                        <CardContent>
                            <Typography variant="subtitle2" color="textSecondary">Aguardando Suporte</Typography>
                            <Typography variant="h4">{waitingSupport}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flex: 1, bgcolor: '#e8f5e9' }}>
                        <CardContent>
                            <Typography variant="subtitle2" color="textSecondary">Aguardando Operação</Typography>
                            <Typography variant="h4">{waitingOp}</Typography>
                        </CardContent>
                    </Card>
                </Stack>

                {/* Detailed Indicators */}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                        <StatCard title="Tempo Médio Fechamento" metricKey="closing" isAvg />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                        <StatCard title="Tempo Máx Fechamento" metricKey="closing" isMax />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                        <StatCard title="Média Espera Suporte" metricKey="support" isAvg />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                        <StatCard title="Média Espera Operação" metricKey="ops" isAvg />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                        <StatCard title="Acumulado Suporte" metricKey="support" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                        <StatCard title="Acumulado Operação" metricKey="ops" />
                    </Grid>
                </Grid>

                <DataTable
                    columns={columns}
                    data={tickets}
                    pagination
                    progressPending={loading}
                    persistTableHead
                />
            </Stack>

            {/* Create Modal */}
            <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Novo Ticket</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <Autocomplete
                            options={["Falha no 4G", "Teclado", "Cartão", "Tela", "Sem sinal de GPS", "Telemetria não liga", "Máquina não liga", "Sistema", "Outro"]}
                            value={tipoProblema || null}
                            onChange={(e, v) => setTipoProblema(v || '')}
                            renderInput={(params) => <TextField {...params} label="Tipo de Problema" required />}
                            freeSolo
                        />

                        <Autocomplete
                            options={deviceOptions}
                            getOptionLabel={(option) => `${option.tipo} - ${option.id} (${option.setor})`}
                            value={selectedDevice}
                            onChange={(e, v) => {
                                setSelectedDevice(v);
                                if (v) setLocalizacaoText(''); // clear manual text if selected
                            }}
                            renderInput={(params) => <TextField {...params} label="Equipamento" placeholder="Busque pelo ID ou Tipo" />}
                        />
                        {!selectedDevice && (
                            <TextField
                                label="Equipamento (Se não listado acima - Preencher manualmente)"
                                value={localizacaoText}
                                onChange={(e) => setLocalizacaoText(e.target.value)}
                                helperText="Use apenas se o equipamento não estiver cadastrado no sistema."
                            />
                        )}

                        <TextField
                            select
                            label="Prioridade"
                            value={priority}
                            onChange={(e) => setPriority(e.target.value)}
                        >
                            {['Low', 'Medium', 'High', 'Critical'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </TextField>

                        <TextField
                            label="Descrição Detalhada"
                            multiline
                            rows={4}
                            value={descricao}
                            onChange={(e) => setDescricao(e.target.value)}
                            required
                        />

                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<AttachFileIcon />}
                        >
                            Anexar Arquivos
                            <input type="file" hidden multiple onChange={(e) => setSelectedFiles(e.target.files)} />
                        </Button>
                        {selectedFiles && <Typography variant="caption">{selectedFiles.length} arquivo(s) selecionado(s)</Typography>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCreate(false)}>Cancelar</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={createLoading}>
                        {createLoading ? 'Criando...' : 'Criar Ticket'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Close Modal */}
            <Dialog open={openClose} onClose={() => setOpenClose(false)} fullWidth maxWidth="sm">
                <DialogTitle>Fechar Ticket</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <Typography gutterBottom>Selecione o motivo do fechamento e adicione detalhes:</Typography>

                        <TextField
                            select
                            label="Motivo do Fechamento"
                            value={closeReason}
                            onChange={(e) => setCloseReason(e.target.value)}
                            fullWidth
                            required
                        >
                            {[
                                "Aberto sem necessidade",
                                "Erro de instalação",
                                "Erro de configuração",
                                "Falha na Telemetria",
                                "Problema na maquina",
                                "Bug de Firmware",
                                "Bug de Hardware",
                                "Bug de Sistema",
                                "Falha temporaria"
                            ].map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                        </TextField>

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Comentário de Fechamento"
                            value={closeMsg}
                            onChange={(e) => setCloseMsg(e.target.value)}
                            required
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenClose(false)}>Cancelar</Button>
                    <Button onClick={handleCloseTicket} variant="contained" color="success" disabled={!closeMsg || !closeReason}>
                        Confirmar Fechamento
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Detail Modal (Chat) */}
            <Dialog open={openDetail} onClose={() => setOpenDetail(false)} maxWidth="md" fullWidth>
                <DialogTitle>Detalhes do Ticket</DialogTitle>
                <DialogContent>
                    {selectedTicket && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4} sx={{ borderRight: '1px solid #ddd' }}>
                                <Typography variant="subtitle2">Tipo</Typography>
                                <Typography variant="body2" gutterBottom>{selectedTicket.tipo_problema}</Typography>

                                <Typography variant="subtitle2">Equipamento</Typography>
                                <Typography variant="body2" gutterBottom>{selectedTicket.localizacao}</Typography>

                                <Typography variant="subtitle2">Prioridade</Typography>
                                <Chip label={selectedTicket.priority} size="small" sx={{ mb: 2 }} />

                                <Typography variant="subtitle2">Descrição</Typography>
                                <Typography variant="body2" paragraph sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                                    {selectedTicket.descricao}
                                </Typography>
                                {renderAttachments(selectedTicket.attachments)}

                                <Typography variant="caption" display="block">
                                    Aberto por: {selectedTicket.created_by} em {new Date(selectedTicket.created_at).toLocaleString()}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={8}>
                                <Typography variant="h6" gutterBottom>Histórico & Comentários</Typography>
                                <Stack spacing={2} sx={{ maxHeight: 300, overflowY: 'auto', mb: 2, p: 1, bgcolor: '#fafafa' }}>
                                    {selectedTicket.comments?.length === 0 && <Typography variant="body2" color="textSecondary">Nenhum comentário ainda.</Typography>}
                                    {selectedTicket.comments?.map((c, idx) => (
                                        <Card key={idx} variant="outlined">
                                            <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                                                <Typography variant="caption" color="primary" fontWeight="bold">
                                                    {c.user} - {new Date(c.created_at).toLocaleString()}
                                                </Typography>
                                                <Typography variant="body2">{c.message}</Typography>
                                                {renderAttachments(c.attachments)}
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>

                                {selectedTicket.status === 'aberto' && (
                                    <Box>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Escreva um comentário..."
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                        />
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
                                            <Button
                                                component="label"
                                                size="small"
                                                startIcon={<AttachFileIcon />}
                                            >
                                                Anexar
                                                <input type="file" hidden multiple onChange={(e) => setCommentFiles(e.target.files)} />
                                            </Button>
                                            <Button variant="contained" size="small" onClick={handleAddComment} disabled={!newComment}>
                                                Enviar
                                            </Button>
                                        </Stack>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDetail(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

        </DrawerHeader>
    );
}
